import { Landmark, PencilIcon, TrashIcon } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { serviceForBankCode,ServiceMark } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { InvoicingBankAccountForm } from '@/forms/InvoicingBankAccountForm'
import API from '@/lib/api'
import { components } from '@invo.space/shared'

export type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

export const formatAccountNumber = (account: InvoicingBankAccount) => {
  if (account.accountNumber && account.bankCode) {
    return `${account.accountNumber}/${account.bankCode}`
  }
  return account.accountNumber || account.iban || ''
}

/** Stav dialogů pro přidání, úpravu a smazání bankovního účtu. */
export const useBankAccountDialogs = (onMutate: () => void) => {
  const { t: tb } = useTranslation(undefined, { keyPrefix: 'settings.bankAccounts' })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<InvoicingBankAccount | null>(null)
  const [deletingAccount, setDeletingAccount] = useState<InvoicingBankAccount | null>(null)

  const openCreate = () => {
    setEditingAccount(null)
    setDialogOpen(true)
  }

  const openEdit = (account: InvoicingBankAccount) => {
    setEditingAccount(account)
    setDialogOpen(true)
  }

  const onDelete = async () => {
    if (!deletingAccount) return
    try {
      await API.delete(`/api/v2/bank-accounts/${deletingAccount.id}`)
      toast.success(tb('delete.success'))
      onMutate()
    } catch {
      toast.error(tb('delete.error'))
    }
    setDeletingAccount(null)
  }

  const dialogs: ReactNode = (
    <>
      <InvoicingBankAccountForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingAccount={editingAccount}
        onSuccess={() => onMutate()}
      />

      <AlertDialog open={!!deletingAccount} onOpenChange={(open) => !open && setDeletingAccount(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tb('delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingAccount?.name ? `Opravdu chcete smazat účet ${deletingAccount.name}?` : tb('delete.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tb('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>{tb('delete.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )

  return { openCreate, openEdit, askDelete: setDeletingAccount, dialogs }
}

export interface BankAccountsTableProps {
  bankAccounts: InvoicingBankAccount[] | undefined
  isLoading?: boolean
  onEdit: (account: InvoicingBankAccount) => void
  onDelete: (account: InvoicingBankAccount) => void
  emptyAction?: ReactNode
  compact?: boolean
}

/** Tabulka bankovních účtů pro fakturaci. */
export const BankAccountsTable = ({ bankAccounts, isLoading, onEdit, onDelete, emptyAction, compact }: BankAccountsTableProps) => {
  const { t: tb } = useTranslation(undefined, { keyPrefix: 'settings.bankAccounts' })

  if (!isLoading && bankAccounts && bankAccounts.length === 0) {
    return <EmptyState icon={Landmark} title={tb('empty')} description="Přidejte účet, na který vám mají odběratelé platit. Jeho číslo se tiskne na faktury." ctaButton={emptyAction} />
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Název</TableHead>
            <TableHead className="hidden md:table-cell">Číslo účtu</TableHead>
            {!compact && <TableHead className="hidden md:table-cell">IBAN</TableHead>}
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading &&
            Array.from({ length: 2 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: compact ? 3 : 4 }).map((__, j) => (
                  <TableCell key={j} className={j === 0 || j === (compact ? 2 : 3) ? undefined : 'hidden md:table-cell'}>
                    <Skeleton className="h-4 w-full max-w-32" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {bankAccounts?.map((account) => (
            <TableRow key={account.id} className="group">
              <TableCell className="w-full max-w-0 md:w-auto md:max-w-none">
                <div className="flex items-center gap-3">
                  <ServiceMark service={serviceForBankCode(account.bankCode) ?? 'bankTransfer'} />
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium">{account.name}</span>
                      {account.isDefault && <Badge variant="secondary">{tb('default')}</Badge>}
                    </div>
                    {account.bic && <p className="hidden text-xs text-muted-foreground tabular md:block">BIC {account.bic}</p>}
                    <p className="truncate text-xs text-muted-foreground tabular md:hidden">
                      {formatAccountNumber(account) || account.iban}
                    </p>
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden tabular md:table-cell">
                {formatAccountNumber(account)}
                {compact && account.iban && account.accountNumber && (
                  <p className="text-xs text-muted-foreground">{account.iban}</p>
                )}
              </TableCell>
              {!compact && <TableCell className="hidden text-muted-foreground tabular md:table-cell">{account.iban || '-'}</TableCell>}
              <TableCell>
                <div className="flex justify-end gap-1 opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <Button variant="ghost" size="icon" aria-label={`Upravit ${account.name}`} onClick={() => onEdit(account)}>
                    <PencilIcon />
                  </Button>
                  <Button variant="ghost" size="icon" aria-label={`Smazat ${account.name}`} onClick={() => onDelete(account)}>
                    <TrashIcon />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
